import type {
  ActionFunction,
  LoaderFunction,
  MetaFunction,
} from '@remix-run/node'
import { json, redirect } from '@remix-run/node'
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react'
import * as React from 'react'
import * as jwt from '~/jwt.server'
import { sendEmail } from '~/postmark.server'
import { CheckCircleIcon } from '@heroicons/react/solid'

import { getUserId, createUserSession } from '~/session.server'

import {
  createInviteForUser,
  createUser,
  getUserByEmail,
} from '~/models/user.server'
import { safeRedirect, validateEmail } from '~/utils'

import logo from '~/images/logo.svg'
import { createCareerPage } from '~/models/careerpage.server'
import { restrictedEmailDomains } from '~/restrictedEmailDomains'
import { addEvent } from '~/models/event.server'
import axios from 'axios'

export const loader: LoaderFunction = async ({ request }) => {
  const userId = await getUserId(request)
  if (userId) return redirect('/app')
  return json({})
}

interface ActionData {
  errors: {
    email?: string
    firstName?: string
    lastName?: string
    companyName?: string
  }
  ok?: boolean
}

export const action: ActionFunction = async ({ request }) => {
  throw new Error('Unavailable')
  const formData = await request.formData()
  const email = formData.get('email') as string
  const redirectTo = safeRedirect(formData.get('redirectTo'), '/')

  if (!validateEmail(email)) {
    return json<ActionData>(
      { errors: { email: 'Email is invalid' } },
      { status: 400 }
    )
  }

  const existingUser = await getUserByEmail(email)
  if (existingUser) {
    return json<ActionData>(
      { errors: { email: 'A user already exists with this email' } },
      { status: 400 }
    )
  }

  const payload = {
    redirectTo,
  }

  const options = {
    // algorithm: 'none',
    expiresIn: '1h',
    audience: email,
    issuer: 'growhire.com',
  }

  const token = jwt.sign(payload, process.env.MAGIC_LINK_SECRET!, options)

  let signupLink = process.env.DOMAIN
    ? process.env.DOMAIN
    : 'https://localhost:3000'
  signupLink += `/join/${token}`

  // @FIXME: @REFACTOR: reserve email address, remove condition for domain

  const emailDomain = email.split('@')[1].trim()

  const isRestrictedEmailDomain = restrictedEmailDomains.includes(emailDomain)

  if (isRestrictedEmailDomain) {
    try {
      await axios({
        method: 'post',
        url: 'https://hooks.slack.com/services/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',
        data: {
          text: `⛔ Blocked signup from domain: ${email}`,
        },
      })
    } catch (error) {
      console.error('Error sending slack notification')
      console.error(error)
    }

    return json<ActionData>(
      { errors: { email: 'Use your work email' } },
      { status: 400 }
    )
  }

  const user = await createUser(email, null, null)

  const invite = await createInviteForUser(user, token)

  await addEvent({
    name: 'signup',
    payload: {
      source: 'app/join',
      email,
      userId: user.id,
      inviteId: invite.id,
    },
  })

  if (process.env.NODE_ENV === 'development') {
    console.log('------------- Signup link ----------------')
    console.log(signupLink)
  }

  // TEMPORARILY disable sending invite email

  // await sendEmail({
  //   to: email,
  //   template: 'signup-landing-page',
  //   variables: {
  //     product_name: 'Growhire',
  //     product_url: 'http://growhire.com',
  //     action_url: signupLink,
  //   },
  // })

  if (process.env.NODE_ENV === 'production') {
    try {
      await axios({
        method: 'post',
        url: 'https://hooks.slack.com/services/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',
        data: {
          text: `🎉 New signup via app/join: ${email}`,
        },
      })
    } catch (error) {
      console.error('Error sending slack notification')
      console.error(error)
    }
  }

  // return json({ ok: true })
  return redirect('/join/thankyou')
}

export const meta: MetaFunction = () => {
  return {
    title: 'Sign Up',
  }
}

export default function Join() {
  // const [searchParams] = useSearchParams();
  // const redirectTo = searchParams.get("redirectTo") ?? undefined;
  const actionData = useActionData() as ActionData
  const emailRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (actionData?.errors?.email) {
      emailRef.current?.focus()
    }
  }, [actionData])

  const isSuccess = actionData?.ok

  return (
    <>
      <div className="flex min-h-full flex-col justify-center bg-gray-100 py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img className="mx-auto h-12 w-auto" src={logo} alt="Workflow" />
          <h2 className="mt-12 text-center text-3xl font-extrabold text-gray-900">
            Create a better candidate experience
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            {/* <Link to="/join" className="font-medium text-teal-600 hover:text-teal-500">
              start your 90-day free trial
            </Link> */}
            <Link
              to="/login"
              className="font-medium text-teal-600 hover:text-teal-500"
            >
              log in if you already have an account
            </Link>
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white px-4 py-8 shadow sm:rounded-lg sm:px-10">
            {isSuccess && (
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircleIcon
                    className="h-5 w-5 text-green-500"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3">
                  <h3 className="text-md font-medium text-green-800">
                    Confirm your email
                  </h3>
                  <div className="text-md mt-2 text-gray-700">
                    <p>We sent you an email with your confirmation link.</p>
                  </div>
                </div>
              </div>
            )}
            {!isSuccess && (
              <form className="space-y-6" action="#" method="POST">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email address
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-teal-500 sm:text-sm"
                    />
                    {actionData?.errors?.email && (
                      <div
                        className="pt-1 text-sm text-red-700"
                        id="email-error"
                      >
                        {actionData.errors.email}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    className="flex w-full justify-center rounded-md border border-transparent bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                  >
                    Sign up
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
